import { createMRTColumnHelper, MRT_ColumnDef } from "material-react-table";
import { Version, VersionCatalog } from "../../types/version";
import {
  publishedIdColumn,
  EntityWithMetadataTableSuffixColumns,
  withFieldProofreadingIndicator,
  createEntityFilterSpec,
} from "./EntityUtils";
import type { FilterSpec } from "../../hooks/useServerFilters";
import { IntellexusDateStructure } from "../../types/intellexusDate";
import EntityReferenceComponent from "../../components/entitiesReferences/EntityReferenceComponent";
import { GetIntellexusDate } from "../../components/forms/common/intellexusDate/IntellexusDateField";
import {
  formatLinksList,
  LinksList,
} from "../../components/forms/common/subForms/LinksListField";
import {
  formatDoxographicalCategory,
  formatStringArray,
} from "../formatters/workFormatters";

const columnHelper = createMRTColumnHelper<Version>();

const formatCatalogValue = (value: VersionCatalog | null | undefined): string => {
  if (!value || (!value.catalog_no && !value.catalog_entry)) return "-";
  if (value.catalog_no && value.catalog_entry) {
    return `${value.catalog_no}: ${value.catalog_entry}`;
  }
  return value.catalog_no || value.catalog_entry || "-";
};

const linkToOtherDatabaseColumn = columnHelper.accessor(
  "link_to_other_databases",
  {
    header: "Links",
    Cell: ({ cell }) => {
      const input = cell.getValue() as LinksList;
      return formatLinksList(input);
    },
  }
);

const dateOfCompositionColumn = columnHelper.accessor("date_of_composition", {
  header: "Date of Composition",
  Cell: ({ cell }) => {
    const intellexusDate = cell.getValue() as IntellexusDateStructure;
    return GetIntellexusDate(intellexusDate);
  },
});

const doxographicalCategoryColumn = columnHelper.accessor(
  "doxographical_category",
  {
    header: "Doxographical Category",
    Cell: withFieldProofreadingIndicator("doxographical_category", ({ cell }) =>
      formatDoxographicalCategory(cell.getValue(), "-")
    ),
  }
);

const baseVersionColumns: MRT_ColumnDef<Version, any>[] = [
  publishedIdColumn,
  columnHelper.accessor("name", {
    header: "Version Name",
    Cell: withFieldProofreadingIndicator("name", ({ cell }) =>
      cell.getValue() || "-"
    ),
  }),
  columnHelper.accessor("work", {
    header: "Work",
    enableColumnFilter: false,
    Cell: ({ cell }) => <EntityReferenceComponent entity={cell.getValue()} />,
  }),
  columnHelper.accessor("completeness", {
    header: "Completeness",
    Cell: withFieldProofreadingIndicator("completeness", ({ cell }) =>
      cell.getValue() || "-"
    ),
  }),
  columnHelper.accessor("version_date", {
    header: "Version Date",
    filterVariant: "text",
    Cell: withFieldProofreadingIndicator("version_date", ({ cell }) => {
      if (!cell.getValue()) return "-";
      const intellexusDate = cell.getValue() as IntellexusDateStructure;
      return GetIntellexusDate(intellexusDate);
    }),
  }),
];

const indicExtraColumns: MRT_ColumnDef<Version, any>[] = [
  linkToOtherDatabaseColumn,
  dateOfCompositionColumn,
  doxographicalCategoryColumn,
  columnHelper.accessor("language", {
    header: "Language",
    Cell: withFieldProofreadingIndicator("language", ({ cell }) => {
      const value = cell.getValue() as string[] | undefined;
      return formatStringArray(value, "-");
    }),
  }),
  columnHelper.accessor("status_of_indic_origin", {
    header: "Status of Indic Origin",
    Cell: withFieldProofreadingIndicator("status_of_indic_origin", ({ cell }) => {
      const value = cell.getValue() as string[] | undefined;
      return formatStringArray(value, "-");
    }),
  }),
];

const tibetanExtraColumns: MRT_ColumnDef<Version, any>[] = [
  linkToOtherDatabaseColumn,
  dateOfCompositionColumn,
  doxographicalCategoryColumn,
  columnHelper.accessor("catalog", {
    header: "Catalog",
    Cell: withFieldProofreadingIndicator("catalog", ({ cell }) =>
      formatCatalogValue(cell.getValue() as VersionCatalog | null | undefined)
    ),
  }),
  columnHelper.accessor("transfered_collection", {
    header: "Transfered Collection",
    Cell: withFieldProofreadingIndicator("transfered_collection", ({ cell }) =>
      cell.getValue() || "-"
    ),
  }),
  columnHelper.accessor("source_language", {
    header: "Source Language",
    Cell: withFieldProofreadingIndicator("source_language", ({ cell }) =>
      cell.getValue() || "-"
    ),
  }),
  columnHelper.accessor("collection", {
    header: "Collection",
    Cell: withFieldProofreadingIndicator("collection", ({ cell }) =>
      cell.getValue() || "-"
    ),
  }),
  columnHelper.accessor("vol_number", {
    header: "Vol. Number",
    Cell: withFieldProofreadingIndicator("vol_number", ({ cell }) =>
      cell.getValue() || "-"
    ),
  }),
  columnHelper.accessor("vol_name", {
    header: "Vol. Name",
    Cell: withFieldProofreadingIndicator("vol_name", ({ cell }) =>
      cell.getValue() || "-"
    ),
  }),
  columnHelper.accessor("folio_start", {
    header: "Folio/Page Start",
    Cell: withFieldProofreadingIndicator("folio_start", ({ cell }) =>
      cell.getValue() || "-"
    ),
  }),
  columnHelper.accessor("folio_end", {
    header: "Folio/Page End",
    Cell: withFieldProofreadingIndicator("folio_end", ({ cell }) =>
      cell.getValue() || "-"
    ),
  }),
];

export const versionColumns = [
  ...baseVersionColumns,
  ...EntityWithMetadataTableSuffixColumns,
];

export const indicVersionColumns = [
  ...baseVersionColumns,
  ...indicExtraColumns,
  ...EntityWithMetadataTableSuffixColumns,
];

export const tibetanVersionColumns = [
  ...baseVersionColumns,
  ...tibetanExtraColumns,
  ...EntityWithMetadataTableSuffixColumns,
];

export const chineseVersionColumns = [
  ...baseVersionColumns,
  linkToOtherDatabaseColumn,
  dateOfCompositionColumn,
  ...EntityWithMetadataTableSuffixColumns,
];

export const versionFilterSpec: FilterSpec = createEntityFilterSpec({
  name: { kind: "string" },
  completeness: { kind: "string" },
  version_date: { kind: "string" },
  source_language: { kind: "string" },
  collection: { kind: "string" },
  vol_number: { kind: "string" },
  vol_name: { kind: "string" },
  folio_start: { kind: "string" },
  folio_end: { kind: "string" },
  link_to_other_databases: { kind: "string" },
  date_of_composition: { kind: "string" },
  doxographical_category: { kind: "string" },
  catalog: { kind: "string" },
  transfered_collection: { kind: "string" },
  language: { kind: "string" },
  status_of_indic_origin: { kind: "string" },
});
